import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './utils/ProtectedRoute';
import AuthPage from './pages/AuthPage';
import LandingPage from './pages/LandingPage';
import OnboardingPage from './pages/OnboardingPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import NotFound from './pages/NotFound';

import DashboardLayout from './layouts/DashboardLayout';
import OverviewPage from './pages/dashboard/Overview';
import TasksPage from './pages/dashboard/TasksPage';
import ProjectsPage from './pages/dashboard/Projects';
import ClientsPage from './pages/dashboard/Clients';
import AnalyticsPage from './pages/dashboard/Analytics';
import UsersPage from './pages/dashboard/Users';
import SettingsPage from './pages/dashboard/Settings';
import ProfilePage from './pages/dashboard/Profile';
import BillingPage from './pages/dashboard/Billing';
import { useAuth } from './contexts/AuthContext';
import { getPlanLimits } from './utils/planConfig';
import { useAuthenticatedUser } from './contexts/AuthenticatedUserInfoContext';
import { isAdmin } from './utils/permissions';

const PlanGatedAnalytics = () => {
  const { organization } = useAuth();
  const { authenticatedUser } = useAuthenticatedUser();
  const limits = getPlanLimits(
    organization?.plan || 'free',
    organization?.trialEndsAt
  );

  if (!isAdmin(authenticatedUser) && !limits.analytics) {
    return <Navigate to={{ pathname: '/', hash: '#pricing' }} replace />;
  }

  return <AnalyticsPage />;
};

const PlanGatedUsers = () => {
  const { organization } = useAuth();
  const { authenticatedUser } = useAuthenticatedUser();
  const limits = getPlanLimits(
    organization?.plan || 'free',
    organization?.trialEndsAt
  );

  if (!isAdmin(authenticatedUser) && !limits.roleManagement) {
    return <Navigate to={{ pathname: '/', hash: '#pricing' }} replace />;
  }

  return <UsersPage />;
};

const AdminGatedSettings = () => {
  const { authenticatedUser } = useAuthenticatedUser();

  if (!isAdmin(authenticatedUser)) {
    return <Navigate to="/dashboard/overview" replace />;
  }

  return <SettingsPage />;
};

const AdminGatedBilling = () => {
  const { authenticatedUser } = useAuthenticatedUser();

  if (!isAdmin(authenticatedUser)) {
    return <Navigate to="/dashboard/overview" replace />;
  }

  return <BillingPage />;
};

const RootNotFoundRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (user) {
    return <Navigate to="/dashboard/404" replace />;
  }

  return <Navigate to="/404" replace />;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<Navigate to={{ pathname: '/', hash: '#pricing' }} replace />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/auth" element={<AuthPage />} />

      <Route
        path="/onboarding"
        element={
          <ProtectedRoute allowedRoles={['admin', 'editor', 'viewer']}>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin', 'editor', 'viewer']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="overview" element={<OverviewPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="project/:id/details" element={<ProjectsPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="task/:id/details" element={<TasksPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="analytics" element={<PlanGatedAnalytics />} />
        <Route path="users" element={<PlanGatedUsers />} />
        <Route path="settings" element={<AdminGatedSettings />} />
        <Route path="billing" element={<AdminGatedBilling />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="404" element={<NotFound inDashboard />} />
        <Route path="*" element={<NotFound inDashboard />} />
      </Route>

      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<RootNotFoundRedirect />} />
    </Routes>
  );
};

export default AppRoutes;
